import { chooseImageryZoom, tileRangesForRegion } from "@map2threejs/core/geo";
import type { LatLng } from "@map2threejs/core/types";

export interface BuildEstimate {
  imageryZoom: number;
  imageryTileCount: number;
  textureWidth: number;
  textureHeight: number;
  textureMemoryMegabytes: number;
  terrainTileCount: number;
  vectorTileCount: number;
  estimatedRequestCount: number;
  estimatedExportMegabytes: { minimum: number; maximum: number };
}

function tileCount(center: LatLng, radiusMeters: number, zoom: number): number {
  return tileRangesForRegion(center, radiusMeters, zoom).reduce(
    (total, range) => total + (range.maxX - range.minX + 1) * (range.maxY - range.minY + 1),
    0,
  );
}

export function estimateBuild(
  center: LatLng,
  radiusMeters: number,
  options: {
    imageryMaxZoom?: number;
    imageryTileSize?: number;
    terrainZoom?: number;
    vectorZoom?: number;
    targetImageryMetersPerPixel?: number;
    maxTextureSize?: number;
    maxRasterTiles?: number;
  } = {},
): BuildEstimate {
  const imageryTileSize = options.imageryTileSize ?? 512;
  const imagery = chooseImageryZoom({
    center,
    radiusMeters,
    minZoom: 0,
    maxZoom: options.imageryMaxZoom ?? 22,
    tileSize: imageryTileSize,
    targetMetersPerPixel: options.targetImageryMetersPerPixel ?? 0.5,
    maxTextureSize: options.maxTextureSize ?? 4096,
    maxTiles: options.maxRasterTiles ?? 64,
  });
  const imageryRanges = tileRangesForRegion(center, radiusMeters, imagery.zoom);
  const imageryTileCount = imageryRanges.reduce(
    (total, range) => total + (range.maxX - range.minX + 1) * (range.maxY - range.minY + 1),
    0,
  );
  const firstRange = imageryRanges[0];
  const columns = firstRange ? firstRange.maxX - firstRange.minX + 1 : 0;
  const rows = firstRange ? firstRange.maxY - firstRange.minY + 1 : 0;
  const textureWidth = columns * imageryTileSize;
  const textureHeight = rows * imageryTileSize;
  const terrainTileCount = tileCount(center, radiusMeters, options.terrainZoom ?? 14);
  const vectorTileCount = tileCount(center, radiusMeters, options.vectorZoom ?? 15);
  const selectedAreaSquareKilometers = Math.PI * radiusMeters * radiusMeters / 1_000_000;
  const estimatedMinimum = Math.max(0.5, selectedAreaSquareKilometers * 8);
  const estimatedMaximum = Math.max(2, selectedAreaSquareKilometers * 40);
  return {
    imageryZoom: imagery.zoom,
    imageryTileCount,
    textureWidth,
    textureHeight,
    textureMemoryMegabytes: textureWidth * textureHeight * 4 / 1024 / 1024,
    terrainTileCount,
    vectorTileCount,
    estimatedRequestCount: imageryTileCount + terrainTileCount + vectorTileCount * 2,
    estimatedExportMegabytes: {
      minimum: Math.round(estimatedMinimum * 10) / 10,
      maximum: Math.round(estimatedMaximum * 10) / 10,
    },
  };
}
